/**
 * Batch sync client for the offline punch queue (API-4379).
 *
 * Uploads the entire queued batch to the existing iOS endpoint in one
 * all-or-nothing request: success means every punch was recorded server-side
 * and the caller may clear the local queue; any failure (network or non-2xx)
 * means nothing was recorded and the caller must retain the queue untouched
 * for the next sync attempt. No idempotency keys, no per-punch retry, no
 * backend changes.
 */

import { env } from '@src/config/env';
import { fetchWithNetworkReporting } from './accountApi';
import type { OfflinePunch } from '@src/config/offlinePunchQueue';

/** Wire value for the sync_offline endpoint's API version header. */
export const SYNC_API_VERSION = '2026-02-01';

interface SyncOfflinePunchPayload {
    location_hash: string;
    employee_id: string;
    time: string;
    role_id: number | null;
    department_id: number | null;
    tips: null;
}

function toPayload(punch: OfflinePunch): SyncOfflinePunchPayload {
    return {
        location_hash: punch.location_hash,
        employee_id: punch.employee_id,
        time: punch.time,
        role_id: punch.role_id,
        department_id: punch.department_id,
        tips: punch.tips
    };
}

/**
 * Uploads the whole batch of queued offline punches for a company. Resolves
 * only on a 2xx status; throws on any non-ok status or a thrown fetch
 * (network failure). The caller treats any throw as "failure — retain the
 * queue". Per the endpoint's documented contract, a successful sync returns
 * HTTP 202 (not 200), so `res.ok` (any 2xx) is the correct success check.
 */
export async function syncOfflinePunches(
    companyId: number,
    punches: OfflinePunch[],
    accessToken: string
): Promise<void> {
    const res = await fetchWithNetworkReporting(
        `${env.apiBaseUrl}/api/v2/company/${companyId}/time_punches/sync_offline`,
        {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'x-api-version': SYNC_API_VERSION
            },
            body: JSON.stringify({
                offline_punches: punches.map(toPayload)
            })
        }
    );

    // The sync_offline endpoint returns 202 (accepted) on success, not 200 —
    // `res.ok` (any 2xx) is the correct check here, not an exact-status
    // comparison.
    if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(
            `Offline punch sync failed (${res.status}): ${body || res.statusText}`
        );
    }
}
